"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateTotalDaysAccountedForSSTType = exports.SSTRequestUserData = exports.SSTRequestUserDataSchema = exports.RequestSSTDTO = exports.requestSSTSectionBase = exports.RequestSSTTypesMappings = exports.VesselDTO = exports.VesselSSTSchema = exports.RequestSSTSchemaMappings = exports.withRequestSSTValidation = exports.InteriorSchema = exports.DualRoleSchema = exports.EngineeringTestimonialSchema = exports.DeckTestimonialSchema = exports.RequestSSTBaseModelSchema = exports.getRequestSSTCapacities = exports.RequestSSTCapacitiesByType = exports.InteriorSSTCapacities = exports.DualRoleSSTCapacities = exports.EngineeringSSTCapacities = exports.DeckSSTCapacities = exports.additionalInformationMessage = exports.RequestSSTTypesArray = void 0;
var zod_1 = require("zod");
var const_1 = require("./const");
var CommonMongoModelFields_1 = require("./lib/CommonMongoModelFields");
var ObjectId_1 = require("./lib/ObjectId");
var RequestSSTToken_1 = require("./RequestSSTToken");
var Signatory_1 = require("./Signatory");
var SST_1 = require("./SST");
var User_1 = require("./User");
var dateUtils_1 = require("./lib/dateUtils");
exports.RequestSSTTypesArray = ['Deck', 'Engineering', 'DualRole', 'Interior'];
var dayjs_1 = __importDefault(require("dayjs"));
var utc_1 = __importDefault(require("dayjs/plugin/utc"));
dayjs_1.default.extend(utc_1.default);
var seaSearviceFutureMessage = 'Sea service dates cannot be set in the future.';
var cruisingAreaMessage = 'Select at least one cruising area.';
// Required to help identify and query fraudulent/inflated sea service claims (client request, July 2026)
exports.additionalInformationMessage = 'Please write a description of your time in the yard and include a jobs list.';
var minValue = function (type, value) {
    if (value === void 0) { value = 0; }
    return "Please enter a ".concat(type, " value greater than ").concat(value, ".");
};
var maxValue = function (type, value) {
    if (value === void 0) { value = 0; }
    return "Please enter a ".concat(type, " value smaller than ").concat(value, ".");
};
var minCapacityMessage = "You must select at least one capacity.";
var maxCapacityMessage = "You can select up to two capacities.";
var capacityMessage = 'Please select a valid capacity';
var capacityMessageObject = { invalid_type_error: capacityMessage, required_error: capacityMessage };
// Single source of truth for SST capacity options per type (schemas + UI mappings derive from these)
exports.DeckSSTCapacities = ['Master', 'Chief Mate', 'OOW', 'Deckhand', 'Chase Boat Captain', 'Bosun'];
exports.EngineeringSSTCapacities = [
    'Chief Engineer',
    'Second Engineer',
    'Engineer Watchkeeper',
    'Engineer',
    'ETO',
];
exports.DualRoleSSTCapacities = [
    'Master',
    'Chief Mate',
    'OOW',
    'Deckhand',
    'Chief Engineer',
    'Second Engineer',
    'Engineer Watchkeeper',
    'Engineer',
];
exports.InteriorSSTCapacities = [
    'Chef',
    'Cook',
    'Steward / ess',
    'Chief steward / ess',
    'Deckhand',
    'Purser',
];
exports.RequestSSTCapacitiesByType = {
    Deck: exports.DeckSSTCapacities,
    Engineering: exports.EngineeringSSTCapacities,
    DualRole: exports.DualRoleSSTCapacities,
    Interior: exports.InteriorSSTCapacities,
};
var getRequestSSTCapacities = function (type) { return exports.RequestSSTCapacitiesByType[type]; };
exports.getRequestSSTCapacities = getRequestSSTCapacities;
var sstCapacityEnum = function (values) {
    return zod_1.z.enum(values, { errorMap: function () { return ({ message: capacityMessage }); } });
};
var buildRequestSSTTypesMappings = function () {
    var id = 1;
    return exports.RequestSSTTypesArray.reduce(function (acc, type) {
        acc[type] = exports.RequestSSTCapacitiesByType[type].map(function (capacity) { return ({
            id: id++,
            capacity: capacity,
        }); });
        return acc;
    }, {});
};
// Base schema for common fields
// Cant use SSTDetailedSchema directly, as it's being used for the SatoriM Migration, and there are nulls everywhere.
var requestSSTBaseSchema = zod_1.z.object({
    sstRequestType: zod_1.z.enum(exports.RequestSSTTypesArray),
    // Ticket #160 - Upload URL was optional but zod was requiring it either way, so I've added a nullish beforehand to deal with this.
    uploadUrl: zod_1.z.string().nullish().optional(),
    // FROM
    capacity: zod_1.z.array(zod_1.z.string()).min(1).max(2),
    // On Board
    vesselType: zod_1.z.string().min(1, '*'),
    vesselName: zod_1.z.string().min(1, '*'),
    officialNumber: zod_1.z.string().min(1, '*'),
    imo: zod_1.z.string().optional(),
    flag: zod_1.z.string().min(1, 'Please select the vessels flag.'),
    loadLine: zod_1.z.coerce.number().min(1, minValue('Load Line Length')),
    grossTonnage: zod_1.z.coerce.number().min(1, minValue('Gross Tonnage')),
    typeOfMainEngine: zod_1.z.string().min(1, '*').optional(),
    mainEnginePropulsionPower: zod_1.z.string().min(1, 'Please enter the propulsion power').optional(), // Not always optional
    // On Board Service
    dateFrom: zod_1.z.coerce.date().refine(function (date) {
        var inputDate = new Date(date);
        var today = new Date();
        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return inputDate <= today;
    }, {
        message: seaSearviceFutureMessage,
    }),
    dateTo: zod_1.z.coerce.date().refine(function (date) {
        var inputDate = new Date(date);
        var today = new Date();
        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return inputDate <= today;
    }, {
        message: seaSearviceFutureMessage,
    }),
    actualDays: zod_1.z.coerce.number().min(0, minValue('Seagoing Service', 0)).optional().default(0), // Actual days at sea
    nauticalMiles: zod_1.z.coerce.number().min(0, minValue('Nautical Miles')).optional().default(0), // Nautical Miles
    daysStandBy: zod_1.z.coerce.number().min(0, minValue('Stand-by Service days')).optional().default(0), // Days of stand-by service
    daysShipyard: zod_1.z.coerce.number().min(0, minValue('Shipyard Service days')).optional().default(0), // Days of shipyard service
    daysWatchKeeping: zod_1.z.coerce.number().min(0, minValue('Watchkeeping days')).optional().default(0), // Watchkeeping days
    daysAdditionalWatchKeeping: zod_1.z.coerce
        .number()
        .min(0, minValue('Engineering Additional Watchkeeping'))
        .optional()
        .default(0), // Watchkeeping days
    daysSeagoingWatchKeeping: zod_1.z.coerce
        .number()
        .min(0, minValue('Engineering Seagoing Watchkeeping or UMS duties'))
        .optional()
        .default(0), // Seagoing watchkeeping or UMS duties for Engineering
    nightWatchHours: zod_1.z.coerce.number().min(0, minValue('Night Watch Hours', 0)).optional().default(0),
    daysGuests: zod_1.z.coerce.number().min(0, minValue('Days with Guests')).optional().default(0), // Days with Guests on Board
    // LOA Dates
    daysLeave: zod_1.z.coerce.number().min(0, minValue('Leave of absence')), // Number of days leave of absence taken
    // Not sure what this equates to.
    rotationProgramOn: zod_1.z.coerce
        .number()
        .min(0, 'Enter a value greater than 0.')
        .max(52, 'Enter a value smaller than 52')
        .optional(), // Rotation program (if applicable)
    rotationProgramOff: zod_1.z.coerce
        .number()
        .min(0, 'Enter a value greater than 0.')
        .max(52, 'Enter a value smaller than 52')
        .optional(), // Rotation program (if applicable)
    areasCruised: zod_1.z.array(zod_1.z.string().min(1), { invalid_type_error: cruisingAreaMessage }).min(1, cruisingAreaMessage),
    // Kept optional at the type level; enforced conditionally in withRequestSSTValidation (see
    // requireAdditionalInformation) so admin review of pre-existing/legacy requests isn't blocked.
    additionalInformation: zod_1.z.string().optional(),
    signatoryEmail: zod_1.z.string().email('Please enter a valid email address.').or(zod_1.z.literal('')).optional().nullish(),
    isAdminCreated: zod_1.z.boolean().optional().default(false),
    status: zod_1.z.union([zod_1.z.string(), zod_1.z.instanceof(ObjectId_1.ObjectId)]).default(const_1.SSTRequestStatusMap.Uploaded),
    // Date when the PYA Office Admins edits the request
    lastAdminEditDate: zod_1.z.coerce.date().optional(),
    lastAdminEdit: zod_1.z.instanceof(ObjectId_1.ObjectId).optional(),
    // Date when the SRB Officers edit the request
    lastSRBOfficerEditDate: zod_1.z.coerce.date().optional(),
    lastSRBOfficerEdit: zod_1.z.instanceof(ObjectId_1.ObjectId).optional(),
    // Date when the RP edits the request
    signedDate: zod_1.z.coerce.date().optional(),
    rejectionComments: zod_1.z.string().optional(),
    // Admin workflow management fields
    assignedAdminId: zod_1.z
        .union([zod_1.z.string(), zod_1.z.instanceof(ObjectId_1.ObjectId)])
        .nullable()
        .optional(),
    adminWorkflowStatus: zod_1.z.enum(const_1.AdminWorkflowStatusArray).default('Pending'),
    assignedAt: zod_1.z.coerce.date().nullable().optional(),
});
exports.RequestSSTBaseModelSchema = requestSSTBaseSchema.extend({
    UserId: zod_1.z.instanceof(ObjectId_1.ObjectId),
    SignatoryId: zod_1.z.instanceof(ObjectId_1.ObjectId).optional(), // Optional - we now use embedded signatoryDetails instead
    signatoryDetails: Signatory_1.SignatoryBaseSchema.nullable().optional(), // Embedded signatory details for data preservation
});
var RequestSSTSchema = exports.RequestSSTBaseModelSchema.extend(CommonMongoModelFields_1.CommonMongoModelFields);
// Deck testimonial schema
exports.DeckTestimonialSchema = requestSSTBaseSchema.extend({
    capacity: zod_1.z.array(sstCapacityEnum(exports.DeckSSTCapacities)).length(1, minCapacityMessage),
    // Fields from other SSTs Types
    // daysSeagoingWatchKeeping: z.undefined(),
    // daysAdditionalWatchKeeping: z.undefined(),
    // mainEnginePropulsionPower: z.undefined(),
    // typeOfMainEngine: z.undefined(),
    // daysGuests: z.undefined(),
    // nauticalMiles: z.undefined(),
});
// Engineering testimonial schema
exports.EngineeringTestimonialSchema = requestSSTBaseSchema.extend({
    capacity: zod_1.z.array(sstCapacityEnum(exports.EngineeringSSTCapacities)).min(1, minCapacityMessage).max(2, maxCapacityMessage),
    // loadLine: z.coerce.number().min(0, minValue('Load Line Length')).nullish().optional(),
    // mainEnginePropulsionPower: z.coerce.number().min(1, minValue('Main Engine Propulsion Power')),
    // typeOfMainEngine: z.undefined(),
    // daysStandBy: z.undefined(),
    // daysGuests: z.undefined(),
});
// Dual role schema
exports.DualRoleSchema = requestSSTBaseSchema.extend({
    capacity: zod_1.z.array(sstCapacityEnum(exports.DualRoleSSTCapacities)).min(1, minCapacityMessage).max(2, maxCapacityMessage),
    // mainEnginePropulsionPower: z.coerce.number().min(1, minValue('Main Engine Propulsion Power')),
    // daysGuests: z.undefined(),
});
// Interior schema — capacity titles are validated against admin-maintained list at parse time
exports.InteriorSchema = requestSSTBaseSchema.extend({
    capacity: zod_1.z.array(zod_1.z.string().min(1)).min(1, minCapacityMessage).max(2, maxCapacityMessage),
    // daysSeagoingWatchKeeping: z.undefined(),
    // daysAdditionalWatchKeeping: z.undefined(),
    // mainEnginePropulsionPower: z.undefined(),
    // typeOfMainEngine: z.undefined(),
});
var withRequestSSTValidation = function (schema, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.requireAdditionalInformation, requireAdditionalInformation = _c === void 0 ? true : _c, interiorAllowedCapacities = _b.interiorAllowedCapacities;
    return schema.superRefine(function (data, ctx) {
        // Signatory email is required for submission (admin-created SSTs bypass this via a separate endpoint)
        if (!data.isAdminCreated && (!data.signatoryEmail || data.signatoryEmail.trim() === '')) {
            ctx.addIssue({
                code: 'custom',
                path: ['signatoryEmail'],
                message: 'Signatory email is required to submit your SST request.',
            });
        }
        if (requireAdditionalInformation && (!data.additionalInformation || data.additionalInformation.trim() === '')) {
            ctx.addIssue({
                code: 'custom',
                path: ['additionalInformation'],
                message: exports.additionalInformationMessage,
            });
        }
        if (new Date(data.dateTo).getTime() <= new Date(data.dateFrom).getTime()) {
            ctx.addIssue({
                path: ['dateTo'], // Path to the problematic field
                message: 'Please ensure that the start date of the service is before the end date.',
                code: 'custom',
            });
        }
        // Calculate total days accounted for based on SST type rules
        var totalDaysAccountedFor = (0, exports.calculateTotalDaysAccountedForSSTType)(data.sstRequestType, data);
        // Use the same calculation pattern as NCAP and SST services: dayjs.diff() + 1
        // This includes both start and end dates in the calculation
        // Use UTC to avoid timezone issues (matching frontend useRemainingDays.ts)
        var totalDaysFromDates = dayjs_1.default.utc(data.dateTo).diff(dayjs_1.default.utc(data.dateFrom), 'days') + 1;
        if (totalDaysAccountedFor > totalDaysFromDates) {
            ctx.addIssue({
                code: 'custom',
                path: ['actualDays'],
                message: "The sum of all accounted days should not exceed the total days on board. The onboard service is set to ".concat(totalDaysFromDates, " days, but ").concat(totalDaysAccountedFor, " days have been entered."),
            });
        }
        var duplicates = data.capacity.filter(function (item, index) { return data.capacity.indexOf(item) !== index; });
        if (duplicates.length > 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['capacity'],
                message: "Please choose distinct capacities.",
            });
        }
        if (data.sstRequestType === 'Interior' && interiorAllowedCapacities !== undefined) {
            var allowed_1 = new Set(interiorAllowedCapacities);
            if (allowed_1.size === 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['capacity'],
                    message: capacityMessage,
                });
            }
            else {
                var invalid = data.capacity.some(function (item) { return !allowed_1.has(item); });
                if (invalid) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['capacity'],
                        message: capacityMessage,
                    });
                }
            }
        }
        // 06/06/25 Email
        // Indeed the values entered in the “Deck Watchkeeping” and “Eng. Seagoing Watchkeeping or UMS duties” fields never be higher than the number entered in “Actual Days at Sea”.
        if (data.daysWatchKeeping > data.actualDays) {
            ctx.addIssue({
                code: 'custom',
                path: ['daysWatchKeeping'],
                message: "Watchkeeping days cannot exceed actual days at sea.",
            });
        }
        if (data.daysSeagoingWatchKeeping > data.actualDays) {
            ctx.addIssue({
                code: 'custom',
                path: ['daysSeagoingWatchKeeping'],
                message: "Eng. Seagoing Watchkeeping or UMS duties days cannot exceed actual days at sea.",
            });
        }
        // JH - March - Feedback
        // New SST - Deck and Dual role - Standby service cannot exceed days at sea - error message required when it does.
        // 06/06/25  And we’ll also make sure the “Standby service cannot be higher than the actual days at sea” check is being applied for Deck SST, Dual Role SST, and Interior SST.
        if (data.sstRequestType === 'Deck' || data.sstRequestType === 'DualRole' || data.sstRequestType === 'Interior') {
            if (data.actualDays < data.daysStandBy) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['daysStandBy'],
                    message: "Standby service cannot exceed days at sea",
                });
            }
        }
    });
};
exports.withRequestSSTValidation = withRequestSSTValidation;
exports.RequestSSTSchemaMappings = {
    Deck: exports.DeckTestimonialSchema,
    Engineering: exports.EngineeringTestimonialSchema,
    DualRole: exports.DualRoleSchema,
    Interior: exports.InteriorSchema,
};
// Vessel Schema
exports.VesselSSTSchema = SST_1.VesselSchema.extend({
    vesselType: zod_1.z.string().min(1, 'Please select a vessel type.'),
    vesselName: zod_1.z.string().min(1, 'Please enter a vessel name.'),
    vesselPhotoURL: zod_1.z.string().optional(),
    typeOfMainEngines: zod_1.z.string().min(1, 'Please enter the main engine type.'),
    mainEnginePropulsionPower: zod_1.z.string().min(1, 'Please enter the propulsion power'),
    loadLine: zod_1.z.coerce
        .number({
        invalid_type_error: 'Please enter a valid Load Line Length',
        required_error: 'Load Line Length is required',
    })
        .min(1, minValue('Load Line Length')),
    flag: zod_1.z.string().min(1, 'Please select the vessels flag.'),
    officialNumber: zod_1.z.string().min(1, 'Please enter the official number.'),
    grossTonnage: zod_1.z.coerce
        .number({
        invalid_type_error: 'Please enter a valid Gross Tonnage',
        required_error: 'Gross Tonnage is required',
    })
        .min(1, minValue('Gross Tonnage')),
    imoNumber: zod_1.z.string().min(1, 'Please enter the IMO number.'),
}).omit({
    name: true,
    length: true,
    mainEngineType: true,
    mainEnginePower: true,
    kw: true,
});
/**
 * HARD CODED some fields due to lack of information
 */
exports.VesselDTO = {
    convertFromModel: function (model) {
        var _a;
        // Normalize vessel type to standard codes (MY/SY)
        var normalizeVesselType = function (vesselType) {
            if (!vesselType)
                return 'MY'; // Default to Motor Yacht code
            var v = vesselType.trim().toUpperCase();
            if (v === 'MY' || v === 'MOTOR YACHT')
                return 'MY';
            if (v === 'SY' || v === 'SAIL YACHT')
                return 'SY';
            return 'MY'; // Default fallback
        };
        return {
            vesselType: normalizeVesselType(model.type),
            vesselName: model.name,
            vesselPhotoURL: '',
            // vesselPhotoURL: 'https://www.yachtfolio2.com/assets/yachfolioBG-BpoEArTo.jpeg', // Not defined source
            officialNumber: model.mmsi || '',
            imoNumber: model.imo || '',
            flag: model.country_code,
            loadLine: model.length,
            grossTonnage: model.gross_tonnage,
            typeOfMainEngines: model.engines_type,
            mainEnginePropulsionPower: (_a = model.main_engines_propulsion_power) === null || _a === void 0 ? void 0 : _a.toString(),
        };
    },
};
// UI/API capacity options — derived from RequestSSTCapacitiesByType to stay in sync with validation
exports.RequestSSTTypesMappings = buildRequestSSTTypesMappings();
exports.requestSSTSectionBase = zod_1.z.object({
    memberId: zod_1.z.instanceof(ObjectId_1.ObjectId),
    memberName: zod_1.z.string().min(1),
    responsibleId: zod_1.z.instanceof(ObjectId_1.ObjectId),
    responsibleName: zod_1.z.string().nullable(),
    sstRequestId: zod_1.z.instanceof(ObjectId_1.ObjectId),
    vesselName: zod_1.z.string().min(1),
    seaTimeFrom: zod_1.z.coerce.date(),
    seaTimeTo: zod_1.z.coerce.date(),
    lastEditAdminName: zod_1.z.string().optional(),
    lastEditAdminDate: zod_1.z.coerce.date().optional(),
    lastEditOfficerName: zod_1.z.string().optional(),
    lastEditOfficerDate: zod_1.z.coerce.date().optional(),
    // Admin workflow fields for list view
    assignedAdminId: zod_1.z.string().nullable().optional(),
    assignedAdminName: zod_1.z.string().nullable().optional(),
    adminWorkflowStatus: zod_1.z.string().optional(),
    signatoryEmail: zod_1.z.string().optional(),
    isAdminCreated: zod_1.z.boolean().optional().default(false),
    // Signatory-link state for the pending-signatory queue. Derived from the newest live token,
    // so it is null for any request that has no outstanding link.
    signatoryTokenExpiry: zod_1.z.coerce.date().nullable().optional(),
    signatoryTokenAgeDays: zod_1.z.number().nullable().optional(),
    signatoryLinkState: zod_1.z.enum(RequestSSTToken_1.SignatoryLinkStates).nullable().optional(),
});
var RequestSSTDTOSchema = RequestSSTSchema.extend({
    Signatory: Signatory_1.SignatorySchema,
    User: User_1.UserSchema,
    RejectedByUser: zod_1.z.string().optional(),
});
var toDate = function (value) {
    if (!value)
        return undefined;
    var dateConverted = value instanceof Date ? value : dayjs_1.default.utc(value).toDate();
    if (!dateConverted)
        return undefined;
    return dateConverted;
};
function getRejectionSource(model) {
    var _a, _b;
    var SEA_SERVICE_TEAM = 'SEA service team';
    var SRB_OFFICER = 'Sea Service Officer';
    var candidates = [
        {
            date: toDate(model.signedDate),
            source: model.Signatory ? "".concat((_a = model.Signatory.firstName) !== null && _a !== void 0 ? _a : '', " ").concat((_b = model.Signatory.lastName) !== null && _b !== void 0 ? _b : '').trim() : null,
        },
        { date: toDate(model.lastAdminEditDate), source: SEA_SERVICE_TEAM },
        { date: toDate(model.lastSRBOfficerEditDate), source: SRB_OFFICER },
    ];
    // Only keep candidates with a valid Date
    var validCandidates = candidates.filter(function (c) { return c.date && !isNaN(c.date.getTime()); });
    if (validCandidates.length === 0) {
        return SEA_SERVICE_TEAM; // fallback default source
    }
    // Pick the one with the latest date
    var latest = validCandidates.reduce(function (a, b) { return (a.date > b.date ? a : b); });
    if (!latest.source) {
        return SEA_SERVICE_TEAM; // fallback default source when name missing
    }
    return latest.source;
}
exports.RequestSSTDTO = {
    convertFromModel: function (model) {
        if (!model)
            return null;
        // Use embedded signatory details if available, otherwise fall back to SignatoryId reference
        model.Signatory = model.signatoryDetails || model.SignatoryId;
        // Check if the request is rejected by the user If can Be signatory Admin or SRBOfficer
        if (model.rejectionComments) {
            model.RejectedByUser = getRejectionSource(model);
        }
        // Convert date fields to strings for frontend
        model.dateFrom = (0, dateUtils_1.dateToDateString)(model.dateFrom);
        model.dateTo = (0, dateUtils_1.dateToDateString)(model.dateTo);
        // Convert numeric propulsion power to string for string-only schema compatibility
        if (model.mainEnginePropulsionPower !== undefined && typeof model.mainEnginePropulsionPower === 'number') {
            model.mainEnginePropulsionPower = model.mainEnginePropulsionPower.toString();
        }
        delete model.SignatoryId;
        return model;
    },
};
exports.SSTRequestUserDataSchema = zod_1.z.object({
    dob: zod_1.z.string(),
    firstName: zod_1.z.string(),
    surname: zod_1.z.string(),
    middleName: zod_1.z.string(),
    userId: zod_1.z.string(),
    email: zod_1.z.string(),
    fullName: zod_1.z.string(),
});
exports.SSTRequestUserData = {
    // Should be UserId.generalSettings & UserId._id
    convertFromModel: function (UserId) {
        var generalSettings = UserId.generalSettings, _id = UserId._id, email = UserId.email, fullName = UserId.fullName;
        if (!_id)
            throw new Error('UserId DTO must have a user id');
        if (!generalSettings || typeof generalSettings === 'string')
            throw new Error('Invalid UserId DTO');
        // Handle DateOfBirth conversion properly
        var dob = '';
        if (generalSettings.DateOfBirth && generalSettings.DateOfBirth.trim() !== '') {
            // Create Date object directly from the string (handles both YYYY-MM-DD and ISO strings)
            var dateObj = new Date(generalSettings.DateOfBirth);
            if (!isNaN(dateObj.getTime())) {
                dob = dateObj.toISOString();
            }
        }
        return {
            dob: dob,
            firstName: generalSettings.FirstName,
            middleName: generalSettings.MiddleNames || '',
            surname: generalSettings.Surname,
            userId: _id,
            email: email || '',
            fullName: fullName || "".concat(generalSettings.FirstName, " ").concat(generalSettings.Surname).trim(),
        };
    },
};
var calculateTotalDaysAccountedForSSTType = function (sstRequestType, data) {
    var n = function (val) { return parseFloat(val) || 0; };
    switch (sstRequestType) {
        case 'Deck':
            // Deck: Actual Days at Sea + Stand-by Service + Leave of Absence + Shipyard Service
            return n(data.actualDays) + n(data.daysStandBy) + n(data.daysLeave) + n(data.daysShipyard);
        case 'Engineering':
            // Engineering: Actual Days at Sea + Leave of Absence + Shipyard Service + Engineering Additional Watchkeeping Days
            return n(data.actualDays) + n(data.daysLeave) + n(data.daysShipyard) + n(data.daysAdditionalWatchKeeping);
        case 'DualRole':
            // DualRole: Actual Days at Sea + Stand-by Service + Leave of Absence + Shipyard Service + Engineering Additional Watchkeeping
            return (n(data.actualDays) +
                n(data.daysStandBy) +
                n(data.daysLeave) +
                n(data.daysShipyard) +
                n(data.daysAdditionalWatchKeeping));
        case 'Interior':
            // Interior: Actual Days at Sea + Stand-by Service + Leave of Absence + Shipyard Service
            // Interior DO NEED to be able to add days into the Deck Watchkeeping box
            return n(data.actualDays) + n(data.daysStandBy) + n(data.daysLeave) + n(data.daysShipyard);
        default:
            return 0;
    }
};
exports.calculateTotalDaysAccountedForSSTType = calculateTotalDaysAccountedForSSTType;
//# sourceMappingURL=RequestSST.js.map